import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom'; // 👈 Import useSearchParams
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import API_BASE_URL, { MEDIA_BASE_URL } from '../config';
import {
  FaArrowRight,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaImages,
  FaVideo,
  FaNewspaper,
  FaCalendarAlt,
  FaCamera,
  FaFolderOpen,
  FaExpand,
} from 'react-icons/fa';
import './Gallery.css';

const getMediaUrl = (img) => {
  if (!img) return null;
  const s = String(img);
  if (s.startsWith('http')) return s;
  if (s.startsWith('/media')) return `${MEDIA_BASE_URL}${s}`;
  if (s.length <= 8 && !s.includes('.')) return null;
  return `${MEDIA_BASE_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const capitalize = (str = '') =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : 'Other';

const Gallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter') || ''; // 👈 Read filter query from URL

  const [activeFilter, setActiveFilter] = useState(urlFilter || 'All');
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [galleryItems, setGalleryItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});

  const { ref: gridRef, inView: gridVisible } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Sync activeFilter with URL parameter
  useEffect(() => {
    if (urlFilter) {
      setActiveFilter(urlFilter);
    } else {
      setActiveFilter('All');
    }
  }, [urlFilter]);

  const handleFilterChange = (filterName) => {
    setActiveFilter(filterName);
    if (filterName === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ filter: filterName });
    }
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/gallery/`);
        const data = res.data || {};

        // const items = (data.gallery || []).map((g) => {
        //   const imgUrl = getMediaUrl(g.image);
        //   const rawCat = g.category_display || g.category || 'other';
        //   return {
        //     id: g.id,
        //     category: capitalize(rawCat),
        //     title: g.title || 'Gallery Item',
        //     emoji: !imgUrl ? g.image || '🖼️' : null,
        //     image: imgUrl,
        //     date: formatDate(g.date),
        //     rawDate: g.date,
        //   };
        // });

        setGalleryItems(data);
        setPartners(
          (data.partners || []).map((p) => p.name || p).filter(Boolean)
        );
      } catch (err) {
        console.error('Error fetching gallery:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // 🎯 DYNAMIC CATEGORIES: Extracted directly from unique categories present in uploaded gallery images
  const categories = useMemo(() => {
    const uniqueCats = [...new Set(galleryItems.map((item) => item.category))].filter(Boolean);
    const cats = uniqueCats.map((name) => ({
      name,
      icon: <FaFolderOpen />,
      count: galleryItems.filter(
        (item) => item.category.toLowerCase() === name.toLowerCase()
      ).length,
    }));
    return [
      { name: 'All', icon: <FaImages />, count: galleryItems.length },
      ...cats,
    ];
  }, [galleryItems]);

  const filteredItems =
    activeFilter === 'All'
      ? galleryItems
      : galleryItems.filter(
          (item) => item.category.toLowerCase() == activeFilter.toLowerCase()
        );

  const stats = useMemo(() => {
    const totalPhotos = galleryItems.length;
    const totalVideos = galleryItems.filter(
      (item) => item.category.toLowerCase().includes('video')
    ).length;
    const totalCategories = categories.length > 1 ? categories.length - 1 : 0;
    const totalMedia = galleryItems.filter((item) =>
      ['event', 'media', 'news'].some((kw) =>
        item.category.toLowerCase().includes(kw)
      )
    ).length;

    return [
      { icon: <FaCamera />, num: totalPhotos || 0, suffix: '+', label: 'Photos Captured' },
      { icon: <FaVideo />, num: totalVideos || 0, suffix: '+', label: 'Video Stories' },
      { icon: <FaFolderOpen />, num: totalCategories || 0, suffix: '+', label: 'Categories' },
      { icon: <FaNewspaper />, num: totalMedia || 0, suffix: '+', label: 'Media Mentions' },
    ];
  }, [galleryItems, categories.length]);

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const nextImage = () =>
    setLightbox((p) => ({
      ...p,
      index: (p.index + 1) % filteredItems.length,
    }));
  const prevImage = () =>
    setLightbox((p) => ({
      ...p,
      index: (p.index - 1 + filteredItems.length) % filteredItems.length,
    }));

  const handleImgError = (id) => {
    setImgErrors((prev) => ({ ...prev, [id]: true }));
  };

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    document.body.style.overflow = lightbox.open ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [lightbox.open]);

  if (loading) {
    return (
      <div className="sg-loader">
        <div className="sg-loader-inner">
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <span className="sg-loader-text">SARWA GROUP</span>
        </div>
      </div>
    );
  }

  const current = filteredItems[lightbox.index];

  const renderImage = (item, className = '') => {
    if (!item.image || imgErrors[item.id]) {
      return (
        <div className="sg-gc-placeholder">
          <FaCamera />
          <span>{item.title}</span>
        </div>
      );
    }
    return (
      <img
        src={item.image}
        alt={item.title}
        className={className}
        loading="lazy"
        onError={() => handleImgError(item.id)}
      />
    );
  };

  return (
    <main className="sg-gallery-page">
      {console.log(activeFilter)}
      {/* HERO SECTION */}
      <section
        className="sg-gallery-hero"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070&auto=format&fit=crop')`,
        }}
      >
        <div className="sg-gallery-hero-overlay"></div>

        <div className="sg-hero-decor">
          <div className="sg-hero-circle sg-hero-circle-1"></div>
          <div className="sg-hero-circle sg-hero-circle-2"></div>
          <div className="sg-hero-line sg-hero-line-1"></div>
          <div className="sg-hero-line sg-hero-line-2"></div>
        </div>

        <div className="container sg-gallery-hero-content">
          <div className="sg-breadcrumb">
            <Link to="/">Home</Link>
            <span className="sg-crumb-sep">/</span>
            <span className="sg-crumb-current">Gallery</span>
          </div>

          <div className="sg-hero-text-center">
            <div className="sg-hero-badge">
              <span className="sg-badge-dot"></span>
              <span>MOMENTS &amp; MILESTONES</span>
              <span className="sg-badge-dot"></span>
            </div>

            <h1 className="sg-gallery-hero-title">
              Our Journey in
              <br />
              <span className="sg-gold-word">Pictures &amp; Stories</span>
            </h1>

            <div className="sg-hero-separator">
              <span className="sg-sep-line"></span>
              <span className="sg-sep-diamond"></span>
              <span className="sg-sep-line"></span>
            </div>

            <p className="sg-gallery-hero-desc">
              A visual chronicle of our growth, achievements, and the memorable
              moments that define Sarwa Group's legacy of excellence across all
              our diversified business verticals.
            </p>

            <a href="#gallery-grid" className="sg-btn-primary">
              <span>Explore Gallery</span>
              <FaArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="sg-gallery-stats">
        <div className="container sg-stats-grid">
          {stats.map((s, i) => (
            <div key={i} className="sg-stat-item">
              <div className="sg-stat-icon">{s.icon}</div>
              <div className="sg-stat-content">
                <h3>
                  {s.num}
                  <span className="sg-stat-suffix">{s.suffix}</span>
                </h3>
                <p>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FILTER TABS */}
      <section className="sg-filter-section">
        <div className="container">
          <div className="sg-filter-header">
            <span className="sg-section-tag">EXPLORE BY BUSINESS</span>
            <h2 className="sg-section-heading">Browse by Division Services</h2>
            <p className="sg-section-subtext">
              Select a specific service/conglomerate to view related gallery images.
            </p>
          </div>

          <div className="sg-filter-tabs">
            {console.log(categories)}
            {categories.map((cat) => (
              <button
                key={cat.name}
                className={`sg-filter-tab ${activeFilter.toLowerCase() === cat.name.toLowerCase() ? 'active' : ''}`}
                onClick={() => handleFilterChange(cat.name)}
              >
                <span className="sg-ft-icon">{cat.icon}</span>
                <span className="sg-ft-name">{cat.name}</span>
                <span className="sg-ft-count">{cat.count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY MASONRY GRID */}
      <section
        className={`sg-gallery-grid-section ${gridVisible ? 'sg-visible' : ''}`}
        id="gallery-grid"
        ref={gridRef}
      >
        <div className="container">
          {filteredItems.length > 0 ? (
            <div className="sg-masonry">
              {filteredItems.map((item, i) => (
                <div
                  key={item.id}
                  className={`sg-gallery-card ${i % 5 === 0 ? 'tall' : ''}`}
                  onClick={() => openLightbox(i)}
                  style={{ animationDelay: `${(i % 8) * 0.08}s` }}
                >
                  <div className="sg-gc-img">
                    {renderImage(item)}
                  </div>
                  <div className="sg-gc-overlay">
                    <div className="sg-gc-info">
                      <span className="sg-gc-cat">{item.category}</span>
                      <h4>{item.title}</h4>
                      <span className="sg-gc-date">
                        <FaCalendarAlt /> {item.date || '—'}
                      </span>
                    </div>
                    <div className="sg-gc-zoom">
                      <FaExpand />
                    </div>
                  </div>
                  <div className="sg-gc-corner"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="sg-no-items">
              <div className="sg-no-items-icon">
                <FaImages />
              </div>
              <h3>No Gallery Items Found</h3>
              <p>
                {galleryItems.length === 0
                  ? 'The gallery is being updated. Please check back soon.'
                  : `No images found for "${activeFilter}". Try selecting a different service.`}
              </p>
              {galleryItems.length > 0 && (
                <button
                  className="sg-btn-primary"
                  onClick={() => handleFilterChange('All')}
                >
                  <span>Show All Items</span>
                  <FaArrowRight />
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="sg-gallery-cta">
        <div className="sg-cta-decor"></div>
        <div className="container sg-cta-inner">
          <div className="sg-cta-content">
            <span className="sg-section-tag light">EXPLORE MORE</span>
            <h2>
              Discover the <span className="sg-gold-word">Corporate Legacy</span>
              <br />Behind Every Frame
            </h2>
            <p>
              Every image tells a story. Explore the businesses, ventures, and
              values that shape the Sarwa Group experience.
            </p>
            <div className="sg-cta-actions">
              <Link to="/services" className="sg-btn-primary">
                <span>Our Businesses</span>
                <FaArrowRight />
              </Link>
              <Link to="/about" className="sg-btn-outline-light">
                <span>About Group</span>
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      {partners.length > 0 && (
        <section className="sg-partners">
          <div className="container">
            <p className="sg-partners-label">TRUSTED BY INDUSTRY LEADERS</p>
            <div className="sg-partners-track-wrap">
              <div className="sg-partners-track">
                {[...partners, ...partners, ...partners].map((p, i) => (
                  <div key={i} className="sg-partner-item">
                    <span>{p.name || p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LIGHTBOX MODAL */}
      {lightbox.open && current && (
        <div className="sg-lightbox" onClick={closeLightbox}>
          <button className="sg-lb-close" onClick={closeLightbox} aria-label="Close">
            <FaTimes />
          </button>
          <div className="sg-lb-counter">
            <span>{String(lightbox.index + 1).padStart(2, '0')}</span>
            <span className="sg-lb-counter-sep">/</span>
            <span>{String(filteredItems.length).padStart(2, '0')}</span>
          </div>
          <button
            className="sg-lb-nav sg-lb-prev"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>
          <div className="sg-lb-content" onClick={(e) => e.stopPropagation()}>
            <div className="sg-lb-image-wrap">
              {current.image && !imgErrors[current.id] ? (
                <img
                  src={current.image}
                  alt={current.title}
                  onError={() => handleImgError(current.id)}
                />
              ) : (
                <div className="sg-lb-placeholder">
                  <FaCamera />
                  <span>{current.title}</span>
                </div>
              )}
            </div>
            <div className="sg-lb-info">
              <span className="sg-lb-cat">{current.category}</span>
              <h3>{current.title}</h3>
              <div className="sg-lb-line"></div>
              <div className="sg-lb-meta">
                <FaCalendarAlt />
                <span>{current.date || '—'}</span>
              </div>
            </div>
          </div>
          <button
            className="sg-lb-nav sg-lb-next"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </main>
  );
};

export default Gallery;